import {Injectable} from '@angular/core';
import {delay} from 'rxjs/operators';
import {SecurityAutocompleteService} from '../../../components/security-autocomplete';
import {
    ErrorCallback,
    Exchange as DatafeedExchange,
    HistoryDepth,
    LibrarySymbolInfo,
    ResolutionBackValues,
    ResolutionString,
    ResolveCallback,
    SearchSymbolsCallback,
    SubscribeBarsCallback
} from '../../../components/trading-view-chart/charting-library/datafeed-api';
import {logMessage} from '../../../components/trading-view-chart/datafeed/helpers';
import {QuotesProvider} from '../../../components/trading-view-chart/datafeed/quotes-provider';
import {Requester} from '../../../components/trading-view-chart/datafeed/requester';
import {
    UdfCompatibleConfiguration,
    UDFCompatibleDatafeedBase
} from '../../../components/trading-view-chart/datafeed/udf-compatible-datafeed-base';
import {CANDLE_PERIOD_MOMENT_DURATION_DICT} from '../../../enums/candle-period';
import {Exchange, EXCHANGE_NAMING} from '../../../enums/exchange';
import {ChartDataPulseProvider} from './chart-data-pulse-provider';
import {ChartHistoryProvider} from './chart-history-provider';
import {CHART_RESOLUTIONS, CHART_RESOLUTIONS_DICT} from './chart-resolution';
import {ChartSecurityService} from './chart-security.service';

const DEFAULT_DATAFEED_URL = 'https://demo_feed.tradingview.com';
const DEFAULT_UPDATE_FREQUENCY = 10 * 1000;

@Injectable()
export class ChartDatafeed extends UDFCompatibleDatafeedBase {

    private readonly chartSecurityAutocompleteService: ChartSecurityService;
    private readonly chartDataPulseProvider: ChartDataPulseProvider;

    constructor(
        chartHistoryProviderService: ChartHistoryProvider,
        securityAutocompleteService: SecurityAutocompleteService,
        chartSecurityAutocompleteService: ChartSecurityService,
        chartDataPulseProvider: ChartDataPulseProvider
    ) {
        const datafeedURL = DEFAULT_DATAFEED_URL;
        const updateFrequency = DEFAULT_UPDATE_FREQUENCY;

        const requester = new Requester();
        const quotesProvider = new QuotesProvider(datafeedURL, requester);

        super(datafeedURL, quotesProvider, requester, chartHistoryProviderService, updateFrequency);

        this.chartSecurityAutocompleteService = chartSecurityAutocompleteService;
        this.chartDataPulseProvider = chartDataPulseProvider;
    }

    public override searchSymbols(
        userInput: string,
        exchange: Exchange,
        symbolType: string,
        onResult: SearchSymbolsCallback
    ): void {
        this.chartSecurityAutocompleteService.searchSymbols({
            userInput,
            exchange,
            symbolType,
            onResult
        });
    }

    public override resolveSymbol(ticker: string, onResolve: ResolveCallback, onError: ErrorCallback): void {
        logMessage('Resolve requested: ' + ticker);

        this.chartSecurityAutocompleteService.resolveSymbol(ticker).pipe(
            delay(0) // Workaround for: `resolveSymbol` should return result asynchronously.
        ).subscribe({
            next:  symbolInfo => symbolInfo
                ? onResolve(symbolInfo)
                : onError('Unknown symbol'),
            error: reason => onError(reason)
        });
    }

    public override calculateHistoryDepth(
        resolution: ResolutionString,
        resolutionBack: ResolutionBackValues,
        intervalBack: number
    ): HistoryDepth | undefined {
        const resolutionDef = CHART_RESOLUTIONS_DICT[resolution];
        const candleDuration = CANDLE_PERIOD_MOMENT_DURATION_DICT[resolutionDef.candlePeriod];

        return candleDuration.asMonths() >= 1
            ? {resolutionBack: 'M' as ResolutionBackValues, intervalBack: 2}
            : candleDuration.asDays() >= 1
                ? {resolutionBack: 'D' as ResolutionBackValues, intervalBack: 2}
                : {resolutionBack: 'D' as ResolutionBackValues, intervalBack: 1};
    }

    public override subscribeBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString,
        onTick: SubscribeBarsCallback,
        listenerGuid: string,
        onResetCacheNeededCallback: () => void
    ): void {
        this.chartDataPulseProvider.subscribeBars(symbolInfo, resolution, onTick, listenerGuid);
    }

    public override unsubscribeBars(listenerGuid: string): void {
        this.chartDataPulseProvider.unsubscribeBars(listenerGuid);
    }

    protected override requestConfiguration(): Promise<UdfCompatibleConfiguration | null> {
        const configuration: UdfCompatibleConfiguration = {
            supports_search:          true,
            supports_group_request:   false,
            supports_marks:           false,
            supports_timescale_marks: false,
            supports_time:            false, // support of server time

            /**
             * Use {@link getConfigurationExchanges()} to add Exchange filter to Symbol search.
             */
            exchanges:                [],

            symbols_types:            [],
            supported_resolutions:    CHART_RESOLUTIONS as ResolutionString[]
        };

        return Promise.resolve(configuration);
    }

    private getConfigurationExchanges(): DatafeedExchange[] {
        return (Object.keys(Exchange) as Exchange[]).map(exchange => ({
            value: exchange,
            name:  EXCHANGE_NAMING[exchange],
            desc:  EXCHANGE_NAMING[exchange]
        }));
    }

}
